/**
 * ProfileSwitcher Component
 *
 * Reusable component for switching between investor profiles in the Hub.
 * Supports two variants:
 * - 'sidebar': Minimal styling for sidebar footer, dropdown appears above
 * - 'dropdown': Styled for use in the user menu dropdown
 */

import { ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState, useCallback } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { HubInvestorProfileSummary } from "@/domain/hub";
import { cn } from "@/utils";
import { ProfileIcon } from "./ProfileSwitcherShared";
import { ProfileSwitcherDropdownVariant } from "./ProfileSwitcherDropdownVariant";

export interface ProfileSwitcherProps {
  selectedProfile: HubInvestorProfileSummary;
  profiles: HubInvestorProfileSummary[];
  onProfileSelect: (uuid: string) => void;
  variant?: "sidebar" | "dropdown";
  isCollapsed?: boolean;
  className?: string;
  onClose?: () => void;
}

export const ProfileSwitcher = ({
  selectedProfile,
  profiles,
  onProfileSelect,
  variant = "sidebar",
  isCollapsed = false,
  className = "",
  onClose,
}: ProfileSwitcherProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const otherProfiles = profiles.filter((p) => p.uuid !== selectedProfile.uuid);
  const hasMultipleProfiles = profiles.length > 1;

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, closeDropdown, isOpen);

  const handleProfileSwitch = useCallback(
    (uuid: string) => {
      if (uuid !== selectedProfile.uuid) {
        onProfileSelect(uuid);
        setIsOpen(false);
        onClose?.();
        navigate("/hub/dashboard", { replace: true });
      }
    },
    [selectedProfile.uuid, onProfileSelect, onClose, navigate],
  );

  const getProfileTypeLabel = (type: string) => {
    return type === "company"
      ? t("hub.profile_selector.company", "Empresa")
      : t("hub.profile_selector.individual", "Particular");
  };

  const toggleDropdown = () => {
    if (hasMultipleProfiles) setIsOpen(!isOpen);
  };
  const switchLabel = t("hub.profile_selector.switch_profile", "Cambiar perfil");

  if (variant === "dropdown") {
    return (
      <ProfileSwitcherDropdownVariant
        selectedProfile={selectedProfile}
        otherProfiles={otherProfiles}
        hasMultipleProfiles={hasMultipleProfiles}
        isOpen={isOpen}
        toggleDropdown={toggleDropdown}
        handleProfileSwitch={handleProfileSwitch}
        getProfileTypeLabel={getProfileTypeLabel}
        dropdownRef={dropdownRef}
        switchLabel={switchLabel}
        className={className}
      />
    );
  }

  // Sidebar variant
  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        onClick={toggleDropdown}
        disabled={!hasMultipleProfiles}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={switchLabel}
        className={cn(
          "w-full flex items-center rounded-md transition-colors duration-200 border border-border-secondary",
          isOpen
            ? "bg-hover-light shadow-md"
            : "bg-bg-primary hover:bg-hover-light hover:shadow-md",
          isCollapsed ? "justify-center p-2" : "px-3 py-2",
          !hasMultipleProfiles ? "cursor-default" : "cursor-pointer",
        )}
      >
        <div className="relative flex-shrink-0">
          <div className="w-7 h-7 rounded-md bg-bg-tertiary flex items-center justify-center">
            <ProfileIcon type={selectedProfile.type} className="text-text-secondary" />
          </div>
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-success-bar rounded-full border-2 border-bg-primary"></div>
        </div>
        {!isCollapsed && (
          <>
            <div className="ml-2 flex-1 min-w-0 text-left">
              <p className="text-xs font-medium text-text-primary truncate">
                {selectedProfile.display_name}
              </p>
              <p className="text-2xs text-text-secondary">
                {getProfileTypeLabel(selectedProfile.type)}
              </p>
            </div>
            {hasMultipleProfiles && (
              <div className="flex flex-col items-center ml-1 flex-shrink-0">
                <ChevronUp
                  size={10}
                  className={cn(
                    "text-text-secondary transition-opacity",
                    isOpen ? "opacity-100" : "opacity-50",
                  )}
                />
                <ChevronDown
                  size={10}
                  className={cn(
                    "text-text-secondary transition-opacity -mt-0.5",
                    isOpen ? "opacity-50" : "opacity-100",
                  )}
                />
              </div>
            )}
          </>
        )}
      </button>

      {isOpen && otherProfiles.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 z-popover" role="listbox">
          <div className="bg-bg-primary border border-border-secondary rounded-md shadow-lg max-h-48 overflow-auto">
            {otherProfiles.map((profile, index) => (
              <button
                key={profile.uuid}
                onClick={() => handleProfileSwitch(profile.uuid)}
                role="option"
                aria-selected={false}
                className={cn(
                  "w-full flex items-center px-3 py-2 hover:bg-hover-light transition-colors",
                  index === 0 && "rounded-t-md",
                  index === otherProfiles.length - 1 && "rounded-b-md",
                )}
              >
                <div className="w-7 h-7 rounded-md bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                  <ProfileIcon type={profile.type} className="text-text-secondary" />
                </div>
                {!isCollapsed && (
                  <div className="ml-2 flex-1 min-w-0 text-left">
                    <p className="text-xs font-medium text-text-primary truncate">
                      {profile.display_name}
                    </p>
                    <p className="text-2xs text-text-secondary">
                      {getProfileTypeLabel(profile.type)}
                    </p>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
